import { infuse } from '../ioc-config';
import { Logger } from './logger.util';
import { querySelectorShadowRoot } from './query-shadow.util';

interface WaitForElementToBeAddedConfig {
  wait?: number;
  wholeDocument?: boolean;
  queryShadowRoots?: boolean;
}

const observerConfig: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
};

export const getRootElement = () => {
  const { componentTag } = infuse('config');

  // For AEM we have to return the shadowRoot of our component
  const elm = document.querySelector(componentTag);

  if (!elm || !elm.shadowRoot) {
    throw new Error(`couldn't find shadowRoot for component ${componentTag}`);
  }

  return elm.shadowRoot;
};

export const queryRootElement = (selector: string) => getRootElement()?.querySelector(selector);

export const waitForElementToBeAdded = (
  selector: string,
  { wait = 2500, wholeDocument = false, queryShadowRoots = false }: WaitForElementToBeAddedConfig = {},
): Promise<Element | null | undefined> => {
  const { componentTag } = infuse('config');
  const rootSelector = wholeDocument
    ? document.body
    : document.querySelector(componentTag)?.shadowRoot ?? document.body;

  return new Promise((resolve) => {
    // fast path if element is already in the DOM
    const target = queryShadowRoots
      ? querySelectorShadowRoot(selector, rootSelector)[0]
      : rootSelector.querySelector(selector);

    if (target) {
      return resolve(target);
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // iterate added nodes
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE || !(node instanceof Element)) return;

            // check if node matches selector
            if (node.matches(selector)) {
              observer.disconnect();

              return resolve(node);
            }

            // if node has shadowRoot, also observe that tree
            if (queryShadowRoots && node.shadowRoot) {
              observer.observe(node.shadowRoot, observerConfig);
            }
          }
        }

        // to find element if target attribute, id or class was added later
        if (mutation.type === 'attributes') {
          if (mutation.target instanceof Element && mutation.target.matches(selector)) {
            observer.disconnect();

            return resolve(mutation.target);
          }
        }
      }
    });

    observer.observe(rootSelector, observerConfig);

    // observe initially existing shadowRoots
    if (queryShadowRoots) {
      for (const node of getAllShadowRoots()) {
        observer.observe(node, observerConfig);
      }
    }

    setTimeout(() => {
      Logger.warn(`Could not find element for selector '${selector}' in reasonable time.`);
      observer.disconnect();

      // return null to mimic querySelector behavior
      return resolve(null);
    }, wait);
  });
};

// Finds the parent shadowRoot for the given element, otherwise returns null.
export const findParentShadowRoot = (el: HTMLElement) => {
  while (el?.nodeType !== 11 /* DOCUMENT_FRAGMENT (shadowRoot) */) {
    if (!el?.parentNode) {
      // el is not inside shadowRoot
      return null;
    }

    el = el.parentNode as HTMLElement;
  }

  return el;
};

const getAllShadowRoots = (): ShadowRoot[] => {
  const result: ShadowRoot[] = [];
  const traverse = (node: Element) => {
    if (node.shadowRoot) {
      result.push(node.shadowRoot);
    }

    const children = node.children;

    if (children.length) {
      for (const child of children) {
        traverse(child);
      }
    }
  };

  traverse(document.body);

  return result;
};
